/**
 * Query key factory for resources covered by SSE invalidation.
 *
 * Convention: keys are model-first (`["ModelName", "scope", ...args]`). Which
 * events refresh which keys is the rules table in `realtime/rules.ts`; a model
 * without a rule refreshes its own family, by id. Models named here MUST match
 * the backend registry `REALTIME_MODELS` in
 * svc-backend/src/scaleupxq/realtime/models.py (a backend test compares them).
 *
 * Families no rule targets are never SSE-invalidated; the mutations that
 * change them refresh them.
 */

export const InvalidatableModels = [
  "AgentRun",
  "AgentRunEvent",
  "BackgroundJob",
  "ContactEnrichmentJob",
  "CrmConnection",
  "CsvImport",
  "DataProviderUsage",
  "InferenceInstance",
  "InferenceRental",
  "MicroApp",
  "Organization",
  "OrganizationMembership",
  "OrganizationTenant",
  "PlatformQueues",
  "PlatformStats",
  "ProspectList",
  "ProspectListBulkAddJob",
  "ProspectListContactEntry",
  "ProspectListCrmExportJob",
  "ProspectListEntry",
  "ProspectOrder",
  "ProspectOrderComment",
  "RecurringJobs",
  "Signal",
  "Tenant",
  "TenantProspect",
  "TrainingDataExport",
  "User",
] as const;

export type InvalidatableModel = (typeof InvalidatableModels)[number];

export const keys = {
  prospectList: {
    all: ["ProspectList"] as const,
    list: (tenantId: string) => ["ProspectList", "list", tenantId] as const,
    detail: (id: string) => ["ProspectList", "detail", id] as const,
    linkSuggestions: (listId: string, entryId: string) =>
      ["ProspectList", "linkSuggestions", listId, entryId] as const,
    overview: (tenantIds: string[], includeArchived = false) =>
      [
        "ProspectList",
        "overview",
        [...tenantIds].sort().join(","),
        includeArchived,
      ] as const,
  },
  prospectListContactEntry: {
    all: ["ProspectListContactEntry"] as const,
    byList: (listId: string) => ["ProspectListContactEntry", "byList", listId] as const,
  },
  prospectOrder: {
    all: ["ProspectOrder"] as const,
    list: (orgId: string, filterKey: string) =>
      ["ProspectOrder", "list", orgId, filterKey] as const,
    detail: (id: string) => ["ProspectOrder", "detail", id] as const,
    agentRuns: (id: string) => ["ProspectOrder", "agentRuns", id] as const,
    workflows: (id: string) => ["ProspectOrder", "workflows", id] as const,
    agentRunPlan: (orgId: string, tenantId: string) =>
      ["ProspectOrder", "agentRunPlan", orgId, tenantId] as const,
  },
  agentCredential: {
    all: ["AgentCredential"] as const,
    list: (orgId: string) => ["AgentCredential", "list", orgId] as const,
    settings: (orgId: string) => ["AgentCredential", "settings", orgId] as const,
  },
  contactEnrichmentJob: {
    all: ["ContactEnrichmentJob"] as const,
    byList: (orgId: string, listId: string) =>
      ["ContactEnrichmentJob", "byList", orgId, listId] as const,
    byOrg: (orgId: string) => ["ContactEnrichmentJob", "byOrg", orgId] as const,
  },
  // Not SSE-invalidated: refreshed by the mutations on the pages that show them.
  contactEnrichmentBilling: {
    limits: (orgId: string) => ["ContactEnrichmentBilling", "limits", orgId] as const,
    usage: (orgId: string, month: string, groupBy: string) =>
      ["ContactEnrichmentBilling", "usage", orgId, month, groupBy] as const,
  },
  dataProvider: {
    all: ["DataProvider"] as const,
    list: () => ["DataProvider", "list"] as const,
    prices: (provider: string) => ["DataProvider", "prices", provider] as const,
    creditRates: (provider: string) => ["DataProvider", "creditRates", provider] as const,
  },
  // Not SSE-invalidated: polled by the News Feeds page and refreshed by its mutations.
  newsFeed: {
    all: ["NewsFeed"] as const,
    list: () => ["NewsFeed", "list"] as const,
  },
  prospectListCrmExportJob: {
    all: ["ProspectListCrmExportJob"] as const,
    latest: (listId: string) => ["ProspectListCrmExportJob", "latest", listId] as const,
  },
  prospectListCrmStatus: {
    all: ["ProspectListCrmStatus"] as const,
    byList: (listId: string) => ["ProspectListCrmStatus", "byList", listId] as const,
  },
  user: {
    all: ["User"] as const,
    list: () => ["User", "list"] as const,
  },
  // Not SSE-invalidated: the account pages' own mutations refresh them.
  account: {
    sessions: () => ["Account", "sessions"] as const,
    connectedApps: () => ["Account", "connectedApps"] as const,
  },
  tenant: {
    all: ["Tenant"] as const,
    list: () => ["Tenant", "list"] as const,
    byOrg: (orgId: string) => ["Tenant", "byOrg", orgId] as const,
    detail: (id: string) => ["Tenant", "detail", id] as const,
    stats: (orgId: string) => ["Tenant", "stats", orgId] as const,
    propertyCatalog: () => ["Tenant", "propertyCatalog"] as const,
    // Polled while a run is active (its stages emit nothing); a Tenant upsert
    // (the run's results) refreshes it too.
    profileRun: (id: string) => ["Tenant", "profileRun", id] as const,
  },
  // Not SSE-invalidated: refreshed by the group mutations.
  userGroup: {
    all: ["UserGroup"] as const,
    list: (orgId: string) => ["UserGroup", "list", orgId] as const,
  },
  // Not SSE-invalidated: refreshed by the saved-view mutations.
  savedView: {
    all: ["SavedView"] as const,
    list: (pageKey: string, tenantId: string | null, orgId: string | null) =>
      ["SavedView", "list", pageKey, tenantId, orgId] as const,
  },
  // Not SSE-invalidated but `stats` (on PlatformStats): changes with data
  // loads; the company page's own mutations refresh what they touch.
  masterCompany: {
    filterOptions: () => ["MasterCompany", "filterOptions"] as const,
    list: (params: object) => ["MasterCompany", "list", params] as const,
    stats: () => ["MasterCompany", "stats"] as const,
    detail: (id: string | undefined) => ["MasterCompany", "detail", id] as const,
    sources: (id: string | undefined) => ["MasterCompany", "sources", id] as const,
    technologies: (id: string | undefined) => ["MasterCompany", "technologies", id] as const,
    financials: (id: string | undefined) => ["MasterCompany", "financials", id] as const,
    taxAssessments: (id: string | undefined) => ["MasterCompany", "taxAssessments", id] as const,
    registryEvents: (id: string | undefined) => ["MasterCompany", "registryEvents", id] as const,
    contacts: (id: string | undefined) => ["MasterCompany", "contacts", id] as const,
    edges: (id: string | undefined) => ["MasterCompany", "edges", id] as const,
    jobAds: (id: string | undefined) => ["MasterCompany", "jobAds", id] as const,
    prospectMemberships: (id: string | undefined) =>
      ["MasterCompany", "prospectMemberships", id] as const,
    newsMentions: (id: string) => ["MasterCompany", "newsMentions", id] as const,
    pressReleases: (id: string, offset: number, limit: number) =>
      ["MasterCompany", "pressReleases", id, offset, limit] as const,
    regulatoryFilings: (id: string, status: string, keyword: string) =>
      ["MasterCompany", "regulatoryFilings", id, status, keyword] as const,
    tenders: (id: string) => ["MasterCompany", "tenders", id] as const,
    crawlPages: (id: string) => ["MasterCompany", "crawlPages", id] as const,
    crawlPageContent: (id: string, page: string | null) =>
      ["MasterCompany", "crawlPageContent", id, page] as const,
    ownershipTree: (id: string, direction: "up" | "down") =>
      ["MasterCompany", "ownershipTree", id, direction] as const,
  },
  // Not SSE-invalidated: refreshed by the contact pages' own mutations.
  masterContact: {
    lists: () => ["MasterContact", "list"] as const,
    list: (params: object) => ["MasterContact", "list", params] as const,
    detail: (id: string | undefined) => ["MasterContact", "detail", id] as const,
    channels: (id: string) => ["MasterContact", "channels", id] as const,
  },
  organization: {
    all: ["Organization"] as const,
    list: () => ["Organization", "list"] as const,
    detail: (id: string) => ["Organization", "detail", id] as const,
    featureFlags: () => ["Organization", "featureFlags"] as const,
    dashboard: (id: string | null, since: string | null) => ["Organization", "dashboard", id, since] as const,
  },
  organizationMembership: {
    all: ["OrganizationMembership"] as const,
    byOrg: (orgId: string) => ["OrganizationMembership", "byOrg", orgId] as const,
  },
  organizationTenant: {
    all: ["OrganizationTenant"] as const,
    byOrg: (orgId: string) => ["OrganizationTenant", "byOrg", orgId] as const,
  },
  crmConnection: {
    all: ["CrmConnection"] as const,
    byOrg: (orgId: string) => ["CrmConnection", "byOrg", orgId] as const,
  },
  // Not SSE-invalidated: refreshed by the Slack integration mutations.
  slackIntegration: {
    all: ["SlackIntegration"] as const,
    byOrg: (orgId: string) => ["SlackIntegration", "byOrg", orgId] as const,
  },
  notion: {
    all: ["Notion"] as const,
    setupInfo: (orgId: string) => ["Notion", "setupInfo", orgId] as const,
    connections: (orgId: string) => ["Notion", "connections", orgId] as const,
    objects: (connectionId: string, query: string) =>
      ["Notion", "objects", connectionId, query] as const,
    scopes: (connectionId: string, tenantId: string) =>
      ["Notion", "scopes", connectionId, tenantId] as const,
  },
  inferenceInstance: {
    all: ["InferenceInstance"] as const,
    list: (status?: string) => ["InferenceInstance", "list", status ?? null] as const,
    fleets: () => ["InferenceInstance", "fleets"] as const,
  },
  inferenceRental: {
    all: ["InferenceRental"] as const,
    list: () => ["InferenceRental", "list"] as const,
    offers: () => ["InferenceRental", "offers"] as const,
    credentials: () => ["InferenceRental", "credentials"] as const,
  },
  agentSkill: {
    all: ["AgentSkill"] as const,
    list: (tenantId: string | null, includeGlobal: boolean) =>
      ["AgentSkill", "list", tenantId, includeGlobal] as const,
  },
  workflow: {
    all: ["Workflow"] as const,
    list: (tenantId: string | null, triggerType: string | null, dataType: string | null) =>
      ["Workflow", "list", tenantId, triggerType, dataType] as const,
    detail: (id: string) => ["Workflow", "detail", id] as const,
  },
  workflowStepType: {
    all: ["WorkflowStepType"] as const,
    list: () => ["WorkflowStepType", "list"] as const,
  },
  agentDefinition: {
    all: ["AgentDefinition"] as const,
    list: (tenantId: string | null) => ["AgentDefinition", "list", tenantId] as const,
    tools: () => ["AgentDefinition", "tools"] as const,
    contracts: () => ["AgentDefinition", "contracts"] as const,
    models: (tenantId: string | null) => ["AgentDefinition", "models", tenantId] as const,
    dataSources: (tenantId: string | null) => ["AgentDefinition", "dataSources", tenantId] as const,
    feedback: (id: string, tenantId: string | null) =>
      ["AgentDefinition", "feedback", id, tenantId] as const,
  },
  workflowTriggerType: {
    all: ["WorkflowTriggerType"] as const,
    list: () => ["WorkflowTriggerType", "list"] as const,
  },
  workflowDataType: {
    all: ["WorkflowDataType"] as const,
    // With a tenant the list carries its choices and availability.
    list: (tenantId: string | null = null) => ["WorkflowDataType", "list", tenantId] as const,
  },
  slackChannels: {
    all: ["SlackChannels"] as const,
    byTenant: (tenantId: string) => ["SlackChannels", "byTenant", tenantId] as const,
  },
  prospect: {
    all: ["TenantProspect"] as const,
    // `filters` is `prospectFilterKey(...)`: one string per distinct filter set.
    list: (filters: string, offset: number) => ["TenantProspect", "list", filters, offset] as const,
    count: (filters: string) => ["TenantProspect", "count", filters] as const,
    countries: (tenantId: string | null) => ["TenantProspect", "countries", tenantId] as const,
    runOptions: (tenantId: string | null, ids: string[]) => ["TenantProspect", "runOptions", tenantId, ids] as const,
    locate: (prospectId: string, filters: string) =>
      ["TenantProspect", "locate", prospectId, filters] as const,
    detail: (prospectId: string) => ["TenantProspect", "detail", prospectId] as const,
    dashboard: (tenantId: string | null, since: string | null) =>
      ["TenantProspect", "dashboard", tenantId, since] as const,
    byCompany: (tenantId: string, companyId: string) =>
      ["TenantProspect", "byCompany", tenantId, companyId] as const,
  },
  signal: {
    all: ["Signal"] as const,
    // `filters` is `signalFilterKey(...)`: one string per distinct filter set.
    list: (filters: string, offset: number) => ["Signal", "list", filters, offset] as const,
    count: (filters: string) => ["Signal", "count", filters] as const,
    countries: (tenantId: string | null) => ["Signal", "countries", tenantId] as const,
    locate: (signalId: string, filters: string) =>
      ["Signal", "locate", signalId, filters] as const,
    dashboard: (tenantId: string | null, workflowId: string | null, since: string | null) =>
      ["Signal", "dashboard", tenantId, workflowId, since] as const,
    research: (signalId: string) => ["Signal", "research", signalId] as const,
  },
  agentRun: {
    transcript: (runId: string) => ["AgentRun", "transcript", runId] as const,
  },
  microApp: {
    all: ["MicroApp"] as const,
    list: (orgId: string | null) => ["MicroApp", "list", "org", orgId ?? "all"] as const,
    nav: (orgId: string | null) => ["MicroApp", "nav", "org", orgId ?? "all"] as const,
    manageList: (orgId: string | null) => ["MicroApp", "manage-list", orgId ?? "all"] as const,
    render: (slug: string | undefined) => ["MicroApp", "render", slug] as const,
    versions: (appId: string | undefined) => ["MicroApp", "versions", appId] as const,
  },
  // `health` refreshes on PlatformStats; `queues` and `signalWorkflows` on
  // PlatformQueues (sampled every five minutes; `queues` also polls for the rigs'
  // heartbeats); `dataProviderUsage` on DataProviderUsage; `recurring` and
  // `recurringTicks` on RecurringJobs. `dataProviderLimits` polls: the
  // account's usage lives at GetLeads, not in our database.
  platform: {
    health: () => ["Platform", "health"] as const,
    queues: () => ["Platform", "queues"] as const,
    signalWorkflows: () => ["Platform", "signalWorkflows"] as const,
    dataProviderUsage: (month: string) => ["Platform", "dataProviderUsage", month] as const,
    dataProviderLimits: () => ["Platform", "dataProviderLimits"] as const,
    recurring: () => ["Platform", "recurring"] as const,
    recurringTicks: (job: string, key: string | null) => ["Platform", "recurringTicks", job, key ?? "all"] as const,
  },
  csvImport: {
    list: () => ["CsvImport", "list"] as const,
  },
  trainingDataExport: {
    list: () => ["TrainingDataExport", "list"] as const,
  },
  backgroundJob: {
    list: () => ["BackgroundJob", "list"] as const,
  },

  // Everything below is not SSE-invalidated (no rule in `realtime/rules.ts`
  // targets it): the mutations that change it, or polling, refresh it.
  microAppGrant: {
    byApp: (appId: string | undefined) => ["MicroAppGrants", appId] as const,
  },
  microAppTvShare: {
    byApp: (appId: string | undefined) => ["MicroAppTvShare", appId] as const,
  },
  microAppSheets: {
    all: ["MicroAppSheets"] as const,
    setup: () => ["MicroAppSheets", "setup"] as const,
    connections: (orgId: string | null) =>
      ["MicroAppSheets", "connections", orgId ?? "none"] as const,
    sources: (orgId: string | null) => ["MicroAppSheets", "sources", orgId ?? "none"] as const,
  },
  tvShare: {
    byCode: (code: string) => ["TvShare", code] as const,
  },
  contactEnrichmentEstimate: {
    quote: (orgId: string, body: object | null) =>
      ["ContactEnrichmentEstimate", orgId, body] as const,
    providers: (orgId: string) => ["ContactEnrichmentEstimate", "providers", orgId] as const,
  },
  crmSequence: {
    byConnection: (connectionId: string) => ["CrmSequence", "byConnection", connectionId] as const,
  },
  prospectListCrmConnection: {
    byList: (listId: string) => ["ProspectListCrmConnection", listId] as const,
  },
  prospectListEnrichPreview: {
    byList: (listId: string | undefined) => ["ProspectListEnrichPreview", listId] as const,
  },
  orgInvitation: {
    byOrg: (orgId: string | null) => ["OrgInvitation", orgId] as const,
  },
  mcpUsage: {
    users14d: () => ["McpUsage", "users-14d"] as const,
  },
  crmIntegration: {
    providers: () => ["CrmIntegration", "providers"] as const,
    mappings: (connectionId: string | undefined) =>
      ["CrmIntegration", "mappings", connectionId] as const,
    pipelines: (connectionId: string | undefined) =>
      ["CrmIntegration", "pipelines", connectionId] as const,
    ownerDomains: (connectionId: string | undefined) =>
      ["CrmIntegration", "ownerDomains", connectionId] as const,
    reps: (connectionId: string | undefined) => ["CrmIntegration", "reps", connectionId] as const,
  },
  tenantData: {
    page: (tenantId: string, objectType: string, limit: number, page: number) =>
      ["TenantData", tenantId, objectType, limit, page] as const,
  },
  cpv: {
    lookup: (lang: string, codes: readonly string[]) => ["Cpv", "lookup", lang, codes] as const,
    search: (lang: string, query: string) => ["Cpv", "search", lang, query] as const,
  },
  mergeSuggestion: {
    lists: () => ["MergeSuggestion", "list"] as const,
    list: (suggestionType: string | null, status: string, page: number, limit?: number) =>
      ["MergeSuggestion", "list", suggestionType, status, page, limit] as const,
    stats: () => ["MergeSuggestion", "stats"] as const,
  },
  jobAd: {
    list: (params: object) => ["JobAd", "list", params] as const,
    detail: (id: string | undefined) => ["JobAd", "detail", id] as const,
    filterOptions: () => ["JobAd", "filterOptions"] as const,
    stats: () => ["JobAd", "stats"] as const,
  },
  tender: {
    list: (params: object) => ["Tender", "list", params] as const,
    detail: (id: string | undefined) => ["Tender", "detail", id] as const,
    filterOptions: () => ["Tender", "filterOptions"] as const,
    stats: () => ["Tender", "stats"] as const,
  },
  newsArticle: {
    list: (params: object) => ["NewsArticle", "list", params] as const,
    detail: (id: string | undefined) => ["NewsArticle", "detail", id] as const,
    filterOptions: () => ["NewsArticle", "filterOptions"] as const,
    stats: () => ["NewsArticle", "stats"] as const,
  },
  regulatoryFiling: {
    list: (params: object) => ["RegulatoryFiling", "list", params] as const,
    detail: (id: string | undefined) => ["RegulatoryFiling", "detail", id] as const,
    filterOptions: () => ["RegulatoryFiling", "filterOptions"] as const,
    stats: () => ["RegulatoryFiling", "stats"] as const,
  },
  atsFeed: {
    list: (params: object) => ["AtsFeed", "list", params] as const,
    filterOptions: () => ["AtsFeed", "filterOptions"] as const,
    stats: () => ["AtsFeed", "stats"] as const,
    crawlStats: () => ["AtsFeed", "crawlStats"] as const,
  },
  systemPrompt: {
    list: () => ["SystemPrompt", "list"] as const,
  },
  aiGenerationLog: {
    list: (params: object) => ["AiGenerationLog", "list", params] as const,
  },
  rolePermission: {
    list: () => ["RolePermission", "list"] as const,
  },
  database: {
    tableStats: () => ["Database", "tableStats"] as const,
  },
} as const;
